import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldToggle,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

// /api/representativetypes — Add (POST), Update (PUT), Delete (PUT), GetById (GET ?Id=), Get (OData liste).
// Kayıt görüntüleme/düzenleme artık tam-sayfa detaya (/representative-types/:id) gider.
const API_BASE = '/api/representativetypes';

const EMPTY_FORM = { code: '', name: '', isMerchant: false };

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }
function toNullableInt(value) {
  if (value === '' || value == null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function RepresentativeTypeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Temsilci Tipi' : 'Temsilci Tipi Detayı', 'Temsilci tipi tanımı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const rowToForm = (r) => ({
    code: pick(r, 'code', 'Code') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    isMerchant: !!pick(r, 'isMerchant', 'IsMerchant'),
  });

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    // Önce navigation state'ten gelen satır; yoksa GetById; o da olmazsa listeden bul.
    const stateRow = location.state?.row;
    if (stateRow && String(stateRow.id) === String(id)) {
      setForm(rowToForm(stateRow));
      // Nav-state satırı liste OData $select'inden gelir → audit alanları eksik.
      // Formu anında doldur; audit'i tam kayıttan (GetById) NON-BLOCKING çek.
      fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => { const rec = unwrap(body); if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') }); })
        .catch(() => {});
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (res.ok) {
        const r = unwrap(await res.json());
        if (r && (pick(r, 'id', 'Id') != null || pick(r, 'name', 'Name') != null)) {
          setForm(rowToForm(r));
          setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
          setLoading(false);
          return;
        }
      }
      // Yedek: OData listesinden id ile bul
      const listRes = await fetch(`${API_BASE}?$top=1000`, { headers: authHeaders() });
      if (listRes.ok) {
        const data = await listRes.json();
        const items = pick(data, 'value', 'Value', 'data', 'Data') ?? (Array.isArray(data) ? data : []);
        const match = (Array.isArray(items) ? items : []).find((x) => String(pick(x, 'id', 'Id')) === String(id));
        if (match) { setForm(rowToForm(match)); setLoading(false); return; }
      }
      throw new Error('Temsilci tipi bulunamadı');
    } catch (err) {
      showToast('error', err.message || 'Temsilci tipi alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /representative-types/new'e kopya veriyle gelindiyse formu doldur
  // (kullanıcı Kaydet diyene kadar HİÇBİR ŞEY kaydedilmez).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildBody = () => ({
    code: toNullableInt(form.code),
    name: String(form.name ?? '').trim(),
    isMerchant: !!form.isMerchant,
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!String(form.name ?? '').trim()) { showToast('error', 'Temsilci tipi adı boş olamaz'); return; }
    setSaving(true);
    try {
      const body = isNew ? buildBody() : { id, ...buildBody() };
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(body),
      });
      const resBody = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(resBody, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Temsilci tipi eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Temsilci tipi başarıyla eklendi');
        setTimeout(() => navigate('/representative-types'), 400);
        return;
      }
      showToast('success', 'Temsilci tipi başarıyla güncellendi');
      setTimeout(() => navigate('/representative-types'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/representative-types/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      showToast('success', 'Temsilci tipi başarıyla silindi');
      setTimeout(() => navigate('/representative-types'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/representative-types')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni temsilci tipi aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Temsilci Tipi', to: '/representative-types' }, { label: isNew ? 'Yeni Temsilci Tipi' : 'Temsilci Tipi Detayı', current: true }]}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'RepresentativeType', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="briefcase" title="Temsilci Tipi Bilgileri" subtitle="Kod ve ad bilgileri">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="code" label="Kod" num type="number" placeholder="Kod giriniz" />
            <FieldText form={form} set={set} tried={tried} k="name" label="Ad" req placeholder="Ad giriniz" />
            {/* TÜCCAR PLASİYER — bu bayrak, temsilci kartındaki "Firma ve E-Belge" ve "Bağlı Tüccarlar"
                sekmelerinin TEK kapısıdır. Karar tipin ADINA değil bayrağa bakar: tenant tipi yeniden
                adlandırınca (ör. "Tüccar Plasiyer" → "Bayi") e-belge akışı bozulmaz. */}
            <FieldToggle
              form={form} set={set} k="isMerchant" span
              label="Tüccar plasiyer tipi"
              hint="Bu tipteki temsilciler kendi VKN'siyle e-belge keser; kartlarında Firma ve E-Belge Ayarları ile Bağlı Tüccarlar sekmeleri açılır."
            />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> temsilci tipini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default RepresentativeTypeDetailPage;
